import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X, Compass } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { rutaInicio } from "@/lib/constants";
import { GUIAS, guiaParaRuta } from "@/lib/guias";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { t } from "@/lib/i18n";

// Prompt M (2026-10-01): guía en pantalla. Se activa con el botón «Guía», explica la pantalla
// actual paso a paso resaltando cada parte, y se cierra en cualquier momento (X, «Salir» o Esc).

const GuiaContext = createContext({ iniciar: () => {}, activa: false });
export const useGuia = () => useContext(GuiaContext);
// Fix R (2026-10-13): la guía del chofer (app en inglés) usa sus textos en inglés; la del panel sigue en español.
const TEXTOS = {
  es: {
    get invitacionAria() { return t("Guía de la plataforma"); }, get primeraVez() { return t("¿Primera vez?"); }, get invitacionTexto() { return t("Te mostramos para qué sirve cada parte, en un minuto."); },
    get cerrar() { return t("Cerrar"); }, get ahoraNo() { return t("Ahora no"); }, get verGuia() { return t("Ver la guía"); }, progreso: (titulo, n, total) => t("{titulo} · {n} de {total}", { titulo, n, total }),
    get cerrarGuia() { return t("Cerrar la guía"); }, get salir() { return t("Salir de la guía"); }, get anterior() { return t("Anterior"); }, get siguiente() { return t("Siguiente"); }, get terminar() { return t("Terminar"); },
  },
};
const textos = () => TEXTOS.es;

const esEscritorio = () => window.matchMedia("(min-width: 1024px)").matches;
const reducirMovimiento = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function visible(el) {
  if (!el) return false;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return false;
  if (r.right <= 0 || r.left >= window.innerWidth) return false;
  const s = window.getComputedStyle(el);
  return s.visibility !== "hidden" && s.display !== "none";
}

function encontrar(sel) {
  if (!sel) return null;
  for (const s of Array.isArray(sel) ? sel : [sel]) {
    for (const el of document.querySelectorAll(s)) {
      if (visible(el)) return el;
    }
  }
  return null;
}

function claveVista(user) {
  return `leonas_guia_vista_${user?.id || "x"}`;
}
function yaVista(user) {
  try { return window.localStorage.getItem(claveVista(user)) === "1"; } catch { return true; }
}
function marcarVista(user) {
  try { window.localStorage.setItem(claveVista(user), "1"); } catch { /* sin almacenamiento */ }
}

export function GuiaProvider({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [guia, setGuia] = useState(null);
  const [paso, setPaso] = useState(0);
  const pendienteRef = useRef(null);
  const [invitar, setInvitar] = useState(false);

  const abrir = useCallback((clave) => {
    const g = GUIAS[clave];
    if (!g) return false;
    const escritorio = esEscritorio();
    const pasos = g.pasos.filter((p) => {
      if (p.roles && !p.roles.includes(user?.rol)) return false;
      if (p.soloEscritorio && !escritorio) return false;
      if (p.soloCelular && escritorio) return false;
      if (p.opcional && !encontrar(p.sel)) return false;
      return true;
    });
    if (!pasos.length) return false;
    setGuia({ titulo: g.titulo, pasos });
    setPaso(0);
    setInvitar(false);
    marcarVista(user);
    return true;
  }, [user]);

  // iniciar(): guía de la pantalla actual. iniciar(clave, ruta): va a esa pantalla y abre esa guía.
  const iniciar = useCallback((clave, ruta) => {
    if (ruta && ruta !== location.pathname) {
      pendienteRef.current = clave;
      navigate(ruta);
      return;
    }
    const c = clave || guiaParaRuta(location.pathname);
    if (!c || !abrir(c)) {
      const inicio = rutaInicio(user);
      const ci = guiaParaRuta(inicio);
      if (inicio !== location.pathname && ci) { pendienteRef.current = ci; navigate(inicio); }
    }
  }, [abrir, location.pathname, navigate, user]);

  const cerrar = useCallback(() => setGuia(null), []);

  const abrirRef = useRef(abrir);
  abrirRef.current = abrir;

  // Al cambiar de pantalla se cierra la guía (o se abre la que quedó pendiente).
  useEffect(() => {
    setGuia(null);
    const c = pendienteRef.current;
    if (!c) return undefined;
    pendienteRef.current = null;
    const tt = setTimeout(() => abrirRef.current(c), 500);
    return () => clearTimeout(tt);
  }, [location.pathname]);

  // Invitación la primera vez que el usuario entra a su pantalla de inicio.
  useEffect(() => {
    if (!user || location.pathname !== rutaInicio(user) || yaVista(user)) { setInvitar(false); return undefined; }
    const tt = setTimeout(() => setInvitar(true), 900);
    return () => clearTimeout(tt);
  }, [user, location.pathname]);

  return (
    <GuiaContext.Provider value={{ iniciar, cerrar, activa: !!guia }}>
      {children}
      {guia && <GuiaOverlay guia={guia} paso={paso} setPaso={setPaso} cerrar={cerrar} />}
      {invitar && !guia && (
        <GuiaInvitacion
          onEmpezar={() => { marcarVista(user); setInvitar(false); iniciar(); }}
          onCerrar={() => { marcarVista(user); setInvitar(false); }}
        />
      )}
    </GuiaContext.Provider>
  );
}

function GuiaInvitacion({ onEmpezar, onCerrar }) {
  const T = textos();
  return (
    <div
      role="dialog"
      aria-label={T.invitacionAria}
      data-testid="guia-invitacion"
      className="fixed z-[80] left-4 right-4 top-4 sm:left-auto sm:top-auto sm:right-6 sm:bottom-6 sm:w-[360px] bg-white border border-line rounded-xl p-4 shadow-[0_12px_32px_rgba(22,24,29,.18)]"
    >
      <div className="flex items-start gap-3">
        <span className="h-9 w-9 rounded-full bg-ink text-white flex items-center justify-center shrink-0"><Compass className="h-5 w-5" /></span>
        <div className="flex-1 min-w-0">
          <p className="font-display font-semibold text-[17px] text-ink leading-tight">{T.primeraVez}</p>
          <p className="text-[14px] text-ink-2 mt-1 leading-snug">{T.invitacionTexto}</p>
        </div>
        <button data-testid="guia-invitacion-cerrar" aria-label={T.cerrar} onClick={onCerrar} className="-mr-1 -mt-1 h-8 w-8 flex items-center justify-center rounded-md text-ink-3 hover:bg-surface-2"><X className="h-4 w-4" /></button>
      </div>
      <div className="flex justify-end gap-2 mt-3">
        <Button data-testid="guia-invitacion-no" variant="outline" className="h-10" onClick={onCerrar}>{T.ahoraNo}</Button>
        <Button data-testid="guia-invitacion-empezar" className="h-10" onClick={onEmpezar}>{T.verGuia}</Button>
      </div>
    </div>
  );
}

function GuiaOverlay({ guia, paso, setPaso, cerrar }) {
  const T = textos();
  const p = guia.pasos[paso];
  const total = guia.pasos.length;
  const ultimo = paso === total - 1;
  const [rect, setRect] = useState(null);
  const cardRef = useRef(null);

  const siguiente = useCallback(() => { if (ultimo) cerrar(); else setPaso((i) => i + 1); }, [ultimo, cerrar, setPaso]);
  const anterior = useCallback(() => setPaso((i) => Math.max(0, i - 1)), [setPaso]);

  useLayoutEffect(() => {
    const el = encontrar(p.sel);
    if (el) el.scrollIntoView({ block: "center", inline: "nearest", behavior: reducirMovimiento() ? "auto" : "smooth" });
    const medir = () => {
      const e = encontrar(p.sel);
      setRect(e ? e.getBoundingClientRect() : null);
    };
    medir();
    const t1 = setTimeout(medir, 250);
    const t2 = setTimeout(medir, 600);
    window.addEventListener("resize", medir);
    window.addEventListener("scroll", medir, true);
    return () => {
      clearTimeout(t1); clearTimeout(t2);
      window.removeEventListener("resize", medir);
      window.removeEventListener("scroll", medir, true);
    };
  }, [p]);

  useEffect(() => { cardRef.current?.focus({ preventScroll: true }); }, [paso]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); cerrar(); }
      else if (e.key === "ArrowRight") { e.preventDefault(); siguiente(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); anterior(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [cerrar, siguiente, anterior]);

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const ancho = Math.min(360, vw - 32);
  const PAD = 6;

  // Recuadro resaltado, recortado a la pantalla.
  let foco = null;
  if (rect) {
    const top = Math.max(rect.top - PAD, 8);
    const bottom = Math.min(rect.bottom + PAD, vh - 8);
    foco = { left: Math.max(rect.left - PAD, 4), top, width: Math.min(rect.width + PAD * 2, vw - 8), height: Math.max(bottom - top, 24) };
  }

  // Posición de la tarjeta: al costado del menú, debajo o arriba del elemento, o al centro.
  let pos;
  if (!foco) {
    pos = { left: (vw - ancho) / 2, top: Math.max(16, vh / 2 - 140) };
  } else if (vw < 640) {
    const centro = foco.top + foco.height / 2;
    pos = centro < vh / 2 ? { left: 16, right: 16, bottom: 16 } : { left: 16, right: 16, top: 16 };
  } else if (foco.left < 300 && foco.width < 300 && foco.left + foco.width + 16 + ancho < vw) {
    pos = { left: foco.left + foco.width + 16, top: Math.min(Math.max(16, foco.top - 8), vh - 280) };
  } else {
    const left = Math.min(Math.max(16, foco.left), vw - ancho - 16);
    if (vh - (foco.top + foco.height) > 260) pos = { left, top: foco.top + foco.height + 12 };
    else if (foco.top > 260) pos = { left, bottom: vh - foco.top + 12 };
    else pos = { left: vw - ancho - 24, bottom: 24 };
  }
  const anim = reducirMovimiento() ? "none" : "all 200ms ease-out";

  return (
    <div className="fixed inset-0 z-[90]" data-testid="guia-overlay">
      {/* Bloquea la pantalla mientras la guía está abierta */}
      <div className="absolute inset-0" onClick={(e) => e.stopPropagation()} />
      {foco ? (
        <div
          data-testid="guia-foco"
          className="absolute rounded-[10px] pointer-events-none"
          style={{ ...foco, boxShadow: "0 0 0 9999px rgba(22,24,29,.62)", outline: "2px solid #FFFFFF", transition: anim }}
        />
      ) : (
        <div className="absolute inset-0 pointer-events-none" style={{ background: "rgba(22,24,29,.62)" }} />
      )}
      <div
        ref={cardRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="guia-titulo"
        data-testid="guia-card"
        className="absolute bg-white rounded-xl border border-line p-5 outline-none shadow-[0_12px_32px_rgba(22,24,29,.24)]"
        style={{ ...pos, width: pos.right !== undefined ? undefined : ancho, transition: anim }}
      >
        <div className="flex items-start justify-between gap-3">
          <p className="eyebrow" data-testid="guia-progreso">{T.progreso(guia.titulo, paso + 1, total)}</p>
          <button data-testid="guia-cerrar" aria-label={T.cerrarGuia} onClick={cerrar} className="-mr-2 -mt-2 h-9 w-9 flex items-center justify-center rounded-md text-ink-3 hover:bg-surface-2"><X className="h-5 w-5" /></button>
        </div>
        <h2 id="guia-titulo" className="font-display font-bold text-[20px] leading-tight text-ink mt-1" data-testid="guia-titulo">{p.titulo}</h2>
        <p className="text-[15px] text-ink-2 mt-2 leading-snug" data-testid="guia-texto">{p.texto}</p>
        <div className="flex flex-wrap gap-1 mt-4" aria-hidden="true">
          {guia.pasos.map((_, i) => (
            <span key={i} className={cn("h-1.5 rounded-full transition-all", i === paso ? "w-5 bg-ink" : "w-1.5 bg-line-strong")} />
          ))}
        </div>
        <div className="flex items-center justify-between gap-2 mt-4">
          <button data-testid="guia-salir" onClick={cerrar} className="text-[14px] text-ink-3 hover:text-ink underline-offset-2 hover:underline">{T.salir}</button>
          <div className="flex gap-2">
            {paso > 0 && <Button data-testid="guia-anterior" variant="outline" className="h-10" onClick={anterior}>{T.anterior}</Button>}
            <Button data-testid="guia-siguiente" className="h-10" onClick={siguiente}>{ultimo ? T.terminar : T.siguiente}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
